import { Button, ConsoleDialog } from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';

import { AddressRecord } from '../../_graphql/queryAccounts';
import * as accountEditingState from '../../_state/accountEditing.state';
import { useTranslations } from '../../i18n/useTranslations';

import { AddressFormFields } from './AddressFormFields';

import { keymap } from '~/shared/keymap';

export type AddressFormDialogProps = {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  address?: AddressRecord;
};

export const AddressFormDialog = ({
  isOpen,
  onOpenChange,
}: AddressFormDialogProps) => {
  const t = useTranslations();

  const canSubmitAddressForm = useAtomValue(
    accountEditingState.$canSubmitAddressForm,
  );
  const { isPending: isUpdatingAccount } = useAtomValue(
    accountEditingState.$updateRemoteAccount,
  );

  const createAccountAddress = useSetAtom(
    accountEditingState.$createAccountAddress,
  );
  const updateAccountAddress = useSetAtom(
    accountEditingState.$updateAccountAddress,
  );

  const addressForm = useAtomValue(accountEditingState.$addressForm);
  const isEditing = !!addressForm.id;

  const hasUnsavedChanges = useAtomValue(
    accountEditingState.$hasUnsavedAddressChanges,
  );

  const handleSubmit = async () => {
    if (isEditing) {
      await updateAccountAddress();
    } else {
      await createAccountAddress();
    }
    onOpenChange(false);
  };

  return (
    <ConsoleDialog
      className="@AddressFormDialog"
      title={
        isEditing
          ? t.accountsView.addresses.dialog.titleEdit
          : t.accountsView.addresses.dialog.titleNew
      }
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      askBeforeClosing={hasUnsavedChanges}
      content={
        <AddressFormFields
          nameLabel={t.accountsView.form.fields.address.name}
          namePlaceholder={t.accountsView.form.placeholders.addressName}
        />
      }
      footer={
        <Button
          isDisabled={!canSubmitAddressForm}
          isLoading={isUpdatingAccount}
          onClick={handleSubmit}
          keyboardShortcut={keymap.save}
        >
          {t.accountsView.dialog.actions.save}
        </Button>
      }
    />
  );
};
